import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BindingAliasSchema } from "@starsach/shared-types";
import { useAccess, useBind, useBindings, useCustomer, useInventory, useProfiles, useUnbind } from "../../api/hooks";
import { Locked } from "../../components/NotHeld";
import { EnvironmentGate, useRememberEnvironment } from "../../components/Environment";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, MutationError, PageHeader, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { moduleById } from "../../lib/modules";
import { ContextBar, InventoryStateNote } from "./ContextBar";
import { TabMenu, type CsvShape } from "./tabs/TabMenu";

/** One bound alias per row, the profile by its name - what a person types in a spreadsheet, not an id. */
type AliasRow = { alias: string; profile: string };
const aliasShape = (profiles: string[]): CsvShape<AliasRow> => ({
  name: "aliases",
  headers: ["alias", "profile"],
  example: [
    { alias: "+16615093462", profile: profiles[0] ?? "Global Helpdesk" },
    { alias: "webchat-support", profile: profiles[0] ?? "Global Helpdesk" },
  ],
  toRow: (r) => ({ alias: r.alias, profile: r.profile }),
  fromRow: (r, line) => {
    const alias = (r["alias"] ?? "").trim().toLowerCase();
    if (!BindingAliasSchema.safeParse(alias).success) return `Line ${line}: "${r["alias"]}" is not an alias - an E.164 number, or lower-case letters, digits, dot, underscore or hyphen.`;
    const profile = (r["profile"] ?? "").trim();
    if (!profile) return `Line ${line}: ${alias} needs the profile it reaches.`;
    if (!profiles.some((p) => p.toLowerCase() === profile.toLowerCase())) return `Line ${line}: no profile named "${profile}" (have: ${profiles.join(", ") || "none"}).`;
    return { alias, profile };
  },
});

/**
 * Section 10.2 and ADR-0020: a binding is what makes a profile reachable
 * - alias -> profile - and sync serves only bound profiles. A phone
 * number from the instance's inventory is one shape of alias, the one
 * Connect hands the lander as the dialled number; a name is the other,
 * for a chat or email flow, or one number that fans into twenty, each
 * with its own hours and announcements. Numbers come from Connect and
 * are never typed; names are typed here and passed by the flow.
 */
export function AliasesPage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="aliases" />;
  return <Aliases environmentId={environmentId} />;
}

function Aliases({ environmentId }: { environmentId: string }) {
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const inventory = useInventory(environmentId);
  const bindings = useBindings(environmentId);
  const bind = useBind(environmentId);
  const unbind = useUnbind(environmentId);
  const canEdit = useAccess().can(environmentId, "configuration.bindings:edit");
  const [name, setName] = useState("");
  const [nameProfile, setNameProfile] = useState("");
  const [problem, setProblem] = useState<string>();
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const numbers = (inventory.data?.items ?? []).filter((i) => i.type === "phone_number").map((i) => i.name);
  const bound = new Map((bindings.data?.bindings ?? []).map((b) => [b.alias, b]));
  // Every number in the instance, bound or not, plus every alias bound by hand - a name, or a number the inventory no longer lists.
  const rows = [...new Set([...numbers, ...bound.keys()])].map((alias) => ({ alias, kind: alias.startsWith("+") ? ("phone_number" as const) : ("name" as const), binding: bound.get(alias), inConnect: numbers.includes(alias) }));
  const busy = bind.isPending || unbind.isPending;
  const list = profiles.data?.profiles ?? [];
  const names = rows.filter((r) => r.kind === "name").length;
  const profileName = (id: string) => list.find((p) => p.profileId === id)?.name ?? id;
  const profileId = (name: string) => list.find((p) => p.name.toLowerCase() === name.toLowerCase())?.profileId;
  const exported: AliasRow[] = [...bound.values()].map((b) => ({ alias: b.alias, profile: profileName(b.profileId) }));
  // Imports bind row by row so each one is its own audited change; "replace" first unbinds whatever the file does not name.
  const importRows = async (items: AliasRow[], replace: boolean) => {
    if (replace) for (const b of bound.values()) if (!items.some((i) => i.alias === b.alias)) await unbind.mutateAsync({ alias: b.alias });
    for (const i of items) {
      const id = profileId(i.profile);
      if (id && bound.get(i.alias)?.profileId !== id) await bind.mutateAsync({ alias: i.alias, profileId: id });
    }
  };
  const unbindAll = async () => {
    for (const b of bound.values()) await unbind.mutateAsync({ alias: b.alias });
  };

  const addName = () => {
    const alias = name.trim().toLowerCase();
    if (!BindingAliasSchema.safeParse(alias).success || alias.startsWith("+")) return setProblem("A name is lower-case letters, digits, dot, underscore or hyphen, up to 64 characters: sales, webchat-support, finance.dob. A phone number comes from Connect, not from here.");
    if (bound.has(alias)) return setProblem(`${alias} is already bound.`);
    if (!nameProfile) return setProblem("Choose the profile the alias reaches.");
    bind.mutate({ alias, profileId: nameProfile }, { onSuccess: () => { setName(""); setNameProfile(""); setProblem(undefined); } });
  };

  return (
    <>
      <PageHeader title="Flow configuration · Aliases" />
      <div className="grid gap-5">
        <ContextBar
          environmentId={environmentId}
          environmentName={envName}
          live={env.data?.customer.live ?? false}
          onEnvironment={(id) => `/flow-configuration/${id}/aliases`}
          actions={bindings.data && <TabMenu what="Aliases" shape={aliasShape(list.map((p) => p.name))} items={exported} canImport={canEdit} canDeleteAll={canEdit} onImport={(items, replace) => void importRows(items, replace)} onDeleteAll={() => void unbindAll()} />}
        >
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {numbers.length} number{numbers.length === 1 ? "" : "s"} · {names} name{names === 1 ? "" : "s"} · {bound.size} bound
            {inventory.data?.observedAt && (
              <>
                {" "}· inventory as of <RelativeTime iso={inventory.data.observedAt} />
              </>
            )}
            <Hint id="flow.alias" label="what an alias is" />
          </span>
        </ContextBar>
        <InventoryStateNote state={inventory.data?.state} environmentName={envName} />
        <p role="note" className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
          A profile is reached by an <strong>alias</strong>. A phone number is one - it comes from your Amazon Connect instance (the <Link to={`/flow-configuration/${environmentId}/cache`} className="underline">Connect cache</Link>), never typed here, and the lander uses the dialled number as the alias on its own. A <strong>name</strong> is the other - for a chat or email flow, or one number that becomes twenty different things - which the flow passes by setting the contact attribute <code>starsach_alias</code> before it invokes the lander or a Starsach module.
        </p>
        {(inventory.isPending || bindings.isPending) && <LoadingState label="Loading aliases" />}
        {bindings.isError && <ErrorState error={bindings.error} onRetry={() => void bindings.refetch()} />}
        {inventory.data && bindings.data && (
          <Card>
            <Locked permission="configuration.bindings:edit" allowed={canEdit}>
            <form
              className="mb-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
              aria-label="Add a named alias"
              onSubmit={(e) => {
                e.preventDefault();
                addName();
              }}
            >
              <label className="text-xs">
                Name
                <input className={`${inputClass} w-56`} value={name} placeholder="webchat-support" aria-label="New alias name" aria-invalid={problem ? true : undefined} onChange={(e) => { setName(e.target.value); setProblem(undefined); }} />
              </label>
              <label className="text-xs">
                Reaches
                <select className={`${inputClass} w-56`} value={nameProfile} aria-label="Profile for the new alias" onChange={(e) => { setNameProfile(e.target.value); setProblem(undefined); }}>
                  <option value="">Choose a profile…</option>
                  {list.map((p) => <option key={p.profileId} value={p.profileId}>{p.name}</option>)}
                </select>
              </label>
              <button type="submit" className={btnPrimary} disabled={busy}>Add alias</button>
              {problem && <p role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
            </form>
            <DataTable
              caption={`Which profile each alias in ${envName} reaches`}
              rows={rows}
              rowKey={(r) => r.alias}
              sortBy={(r) => `${r.kind === "name" ? "0" : "1"}${r.alias}`}
              filter={rows.length > 3}
              empty={<EmptyState title="No aliases yet" hint="The instance's phone numbers arrive with its inventory; a name is added above." />}
              columns={[
                { key: "alias", header: "Alias", render: (r) => <span className="font-medium">{r.alias}</span> },
                { key: "kind", header: "Kind", render: (r) => <StatusPill status={r.kind === "phone_number" ? (r.inConnect ? "phone number" : "phone number, not in connect") : "name"} tone={r.kind === "phone_number" && !r.inConnect ? "warning" : "neutral"} /> },
                {
                  key: "reaches",
                  header: "Reaches",
                  render: (r) => (
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="sr-only">Profile for {r.alias}</span>
                      <select
                        className={`${inputClass} mt-0 w-56`}
                        value={r.binding?.profileId ?? ""}
                        disabled={busy}
                        onChange={(e) => (e.target.value ? bind.mutate({ alias: r.alias, profileId: e.target.value }) : unbind.mutate({ alias: r.alias }))}
                      >
                        <option value="">Not bound</option>
                        {list.map((p) => (
                          <option key={p.profileId} value={p.profileId}>{p.name}</option>
                        ))}
                        {r.binding && !list.some((p) => p.profileId === r.binding!.profileId) && <option value={r.binding.profileId}>{r.binding.profileId}</option>}
                      </select>
                      {!r.binding && <StatusPill status="not bound" tone="warning" />}
                    </label>
                  ),
                },
                { key: "since", header: "Since", render: (r) => (r.binding ? <><RelativeTime iso={r.binding.boundAt} /> by {r.binding.boundBy}</> : "-") },
                {
                  key: "action",
                  header: "",
                  render: (r) =>
                    r.binding ? (
                      <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => unbind.mutate({ alias: r.alias })}>
                        {r.kind === "name" ? "Remove" : "Unbind"}
                      </button>
                    ) : null,
                },
              ]}
            />
            </Locked>
            <MutationError of={[bind, unbind]} className="mt-3" />
          </Card>
        )}
      </div>
    </>
  );
}
